import React, { useState, useEffect, useMemo } from "react";
import { message } from "antd";
import axios from "axios";
import { Area } from "@ant-design/plots";
import {
  Shield,
  ShieldCheck,
  Check,
  X,
  ArrowUp,
  ArrowDown,
  ArrowRight,
  ScrollText,
  FileText,
  UserPlus,
  Monitor,
  UsersRound,
  Server,
} from "lucide-react";
import {
  AUTH_TOKEN,
  DOMAIN_ID,
  ORG_ID,
  REACT_APP_API,
  REACT_APP_API_DID_URL,
  REACT_APP_API_TENANTS_URL,
} from "../constants";
import { logOutUser } from "../common";
import { Navigate, useNavigate } from "react-router-dom";
import SetupTourChecklist from "./pam/SetupTour";
import { AN_COLORS, AN_FONT, AN_MONO, anAlpha } from "../design-system/tokens";
import { useAnTheme } from "../theme/AnThemeProvider";
import { dispatchGetAuthLog } from "../utils/apiservice";
import { DECISION_META, countByDecision, bucketTimeline, dailyTimeline } from "../utils/authLog";
import { getLocalStorage } from "../utils/helpers";
import PamDashboard from "../pages/enduser/pam-dashboard/PamDashboard";
import { listRadiusServers, serverIsActive } from "../service/radiusService";

// PAM modes: the sidebar's "PAM" surface and the PAM entry in the MFA switcher.
// The bottom activity card follows the mode — in PAM it plots PAM session
// events from /policyService/AuthenticationLog, not AD authentications from
// /ad/GetAuthLog. They are different logs, and labelling one with the other's
// name is how people end up debugging the wrong system.
//
// NOTE: "View all" goes to /pam/auth-logs because that is the only logs screen
// PAM lists — but that screen reads /ad/GetAuthLog. The card and its link
// therefore show different data. See the open question in the handover.
const PAM_MODES = ["pam", "mfa_pam"];

// RADIUS mode (sidebar switcher writes currentMode "mfa_radius"). Only in this mode: Active
// Policies counts RADIUS policies (listActivePolicy mode "radius"), MFA required is the
// server's count of challenged RADIUS logins, and a RADIUS servers tile shows N / M active.
const RADIUS_MODE = "mfa_radius";

const Card = ({ title, subtitle, action, children, padding = 18, style = {} }) => (
  <div
    style={{
      background: "var(--an-surface)",
      border: `1px solid ${AN_COLORS.line}`,
      borderRadius: 10,
      overflow: "hidden",
      display: "flex",
      flexDirection: "column",
      boxShadow: "0 1px 2px rgba(16,22,60,.03)",
      ...style,
    }}
  >
    {(title || action) && (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "14px 18px 12px",
          gap: 12,
        }}
      >
        <div>
          {title && (
            <div
              style={{
                fontSize: 13.5,
                fontWeight: 600,
                color: AN_COLORS.ink,
                letterSpacing: -0.1,
              }}
            >
              {title}
            </div>
          )}
          {subtitle && (
            <div style={{ fontSize: 12, color: AN_COLORS.muted, marginTop: 2 }}>
              {subtitle}
            </div>
          )}
        </div>
        {action}
      </div>
    )}
    <div style={{ padding, flex: 1 }}>{children}</div>
  </div>
);

const SectionHeader = ({ eyebrow, title, subtitle, icon: IconCmp, accent = AN_COLORS.primary, action }) => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 12,
      paddingBottom: 12,
      borderBottom: `1px solid ${AN_COLORS.line}`,
      marginTop: 4,
    }}
  >
    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
      {IconCmp && (
        <span
          style={{
            width: 34,
            height: 34,
            borderRadius: 9,
            background: `${anAlpha(accent, 8)}`,
            color: accent,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <IconCmp size={18} />
        </span>
      )}
      <div>
        {eyebrow && (
          <div
            style={{
              fontSize: 10.5,
              fontFamily: AN_MONO,
              letterSpacing: 1.2,
              textTransform: "uppercase",
              color: accent,
              fontWeight: 600,
              marginBottom: 2,
            }}
          >
            {eyebrow}
          </div>
        )}
        <div style={{ fontSize: 17, fontWeight: 650, letterSpacing: -0.3, color: AN_COLORS.ink }}>
          {title}
        </div>
        {subtitle && (
          <div style={{ fontSize: 12.5, color: AN_COLORS.muted, marginTop: 2 }}>{subtitle}</div>
        )}
      </div>
    </div>
    {action}
  </div>
);

const Sparkline = ({ data, color = AN_COLORS.primary, w = 140, h = 32, filled = true, fluid = false }) => {
  if (!data || data.length < 2) return null;
  const max = Math.max(...data),
    min = Math.min(...data);
  const pad = 2;
  const pts = data.map((v, i) => {
    const x = pad + (i / (data.length - 1)) * (w - pad * 2);
    const y = h - pad - ((v - min) / Math.max(1, max - min)) * (h - pad * 2);
    return [x, y];
  });
  const d = pts.map(([x, y], i) => `${i ? "L" : "M"}${x.toFixed(1)} ${y.toFixed(1)}`).join(" ");
  const area = filled ? `${d} L${w - pad} ${h - pad} L${pad} ${h - pad} Z` : null;
  const last = pts[pts.length - 1];
  return (
    <svg
      width={fluid ? "100%" : w}
      height={h}
      viewBox={`0 0 ${w} ${h}`}
      preserveAspectRatio="none"
      style={fluid ? { display: "block" } : undefined}
    >
      {filled && <path d={area} fill={color} fillOpacity="0.08" />}
      <path
        d={d}
        fill="none"
        stroke={color}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx={last[0]} cy={last[1]} r="2.2" fill={color} />
    </svg>
  );
};

// `warning` gives the tile the warn tint (e.g. a RADIUS server is inactive).
const KPI = ({ label, value, delta, sub, icon: IconCmp, accent = AN_COLORS.primary, spark, warning = false }) => {
  const up = delta && String(delta).trim().startsWith("+");
  return (
    <div
      style={{
        padding: 18,
        display: "flex",
        flexDirection: "column",
        gap: 8,
        height: "100%",
        background: warning ? "var(--an-warnBg)" : "var(--an-surface)",
        border: `1px solid ${warning ? "var(--an-warnBd)" : AN_COLORS.line}`,
        borderRadius: 10,
        boxShadow: "0 1px 2px rgba(16,22,60,.03)",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        {IconCmp && (
          <span
            style={{
              width: 26,
              height: 26,
              borderRadius: 7,
              background: `${anAlpha(accent, 8)}`,
              color: accent,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <IconCmp size={14} />
          </span>
        )}
        <div style={{ fontSize: 12, color: AN_COLORS.muted, fontWeight: 500 }}>{label}</div>
      </div>
      <div style={{ display: "flex", alignItems: "baseline", gap: 10 }}>
        <div
          style={{
            fontSize: 28,
            fontWeight: 600,
            color: AN_COLORS.ink,
            letterSpacing: -0.6,
            fontFamily: AN_FONT,
          }}
        >
          {value ?? "—"}
        </div>
        {delta != null && (
          <span
            style={{
              fontSize: 11.5,
              fontWeight: 600,
              fontFamily: AN_MONO,
              color: up ? AN_COLORS.success : AN_COLORS.danger,
              display: "inline-flex",
              alignItems: "center",
              gap: 2,
            }}
          >
            {up ? <ArrowUp size={11} /> : <ArrowDown size={11} />}
            {delta}
          </span>
        )}
      </div>
      {sub && (
        <div style={{ fontSize: 11.5, color: warning ? "var(--an-warnFg)" : AN_COLORS.muted, marginTop: "auto" }}>{sub}</div>
      )}
      {spark && <div style={{ marginTop: sub ? 0 : "auto" }}>{spark}</div>}
    </div>
  );
};

const LegacyDashboard = ({ mode }) => {
  const [, notificationContext] = message.useMessage();
  const [period] = useState("month");
  const [endpointCount, setEndpointCount] = useState(null);
  const [endpointUserCount, setEndpointUserCount] = useState(null);
  const [directoryUsersCount, setDirectoryUsersCount] = useState(null);
  const [tenantData, setTenantData] = useState(null);
  const [policyCount, setPolicyCount] = useState(null);
  const [isShowTour, setIsShowTour] = useState(true);
  const [authTotal, setAuthTotal] = useState(0);
  const [authCounts, setAuthCounts] = useState({ allow: 0, deny: 0, mfa_required: 0, total: 0 });
  const [authTimeline, setAuthTimeline] = useState([]);
  const [pamTimeline, setPamTimeline] = useState([]);
  const isPamMode = PAM_MODES.includes(getLocalStorage("currentMode") || "");
  const isRadiusMode = mode === RADIUS_MODE;
  // RADIUS mode only: challenged RADIUS logins (server count) and the server registry.
  const [radiusChallenged, setRadiusChallenged] = useState(null);
  const [radiusServers, setRadiusServers] = useState(null);
  const navigate = useNavigate();

  const periodDays = period === "day" ? 7 : period === "month" ? 30 : 1;
  const headers = { withCredentials: true, "X-Authorization": AUTH_TOKEN() };

  const handle401 = (err) => {
    if (err?.response?.status === 401) logOutUser();
  };

  useEffect(() => {
    if (!AUTH_TOKEN()) return;
    const payload = { orgId: ORG_ID(), tenantId: DOMAIN_ID() };
    axios
      .post(`${REACT_APP_API_TENANTS_URL}/getTenantStatus`, payload, { headers })
      .then((res) => setIsShowTour(res?.data?.overall_status))
      .catch((err) => handle401(err));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const payload = {
      email: localStorage.getItem("UserName"),
      tenantId: DOMAIN_ID(),
      orgId: ORG_ID(),
    };
    axios
      .post(`${REACT_APP_API_TENANTS_URL}/getTenantDetail`, payload, { headers })
      .then((res) => {
        const policyMode = res?.data?.data?.authenticationpolicy;
        setTenantData(policyMode === 1 ? "Audit" : policyMode === 2 ? "Policy" : "Live");
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!AUTH_TOKEN()) return;
    const payload = { orgId: ORG_ID(), tenantId: DOMAIN_ID(), period: periodDays };
    // RADIUS policies live in auth_policy_json; mode "radius" counts the approved ones in force.
    if (isRadiusMode) payload.mode = "radius";
    const endpointPayload = { orgId: ORG_ID(), domainId: DOMAIN_ID(), period: periodDays };

    axios
      .post(`${REACT_APP_API_DID_URL}/dashboardService/listActivePolicy`, payload, { headers })
      .then((res) => setPolicyCount(res?.data?.totalCount))
      .catch((err) => handle401(err));

    // Gateway authentication events quick view (Duo-style).
    // No time filter — show all-time AD events, matching the /pam/auth-logs page.
    dispatchGetAuthLog({
      payload: {
        orgId: ORG_ID(),
        tenantId: DOMAIN_ID(),
        page: 1,
        pageSize: 1000,
      },
      onSuccess: (data) => {
        setAuthTotal(data?.total || 0);
        setAuthCounts(countByDecision(data?.events));
        setAuthTimeline(data?.timeline || []);
      },
      onFailure: ({ isLogout }) => {
        if (isLogout) logOutUser();
      },
      onError: () => {},
    });

    if (isRadiusMode) {
      // MFA required = logins that raised a push, counted by the server (challenged: true)
      // rather than from the first page of rows. excludeIntermediate keeps a login that also
      // logged an mfa_required row from counting twice. Plain axios, not dispatchGetAuthLog:
      // that helper shares one AbortController per endpoint, so a second call would cancel
      // the main log request above.
      axios
        .post(
          `${REACT_APP_API}/ad/GetAuthLog`,
          {
            orgId: ORG_ID(),
            tenantId: DOMAIN_ID(),
            mode: "radius",
            challenged: true,
            excludeIntermediate: true,
            page: 1,
            pageSize: 1,
          },
          { headers },
        )
        .then(({ data }) => {
          if (data?.code === 200 || data?.status === "Success") setRadiusChallenged(Number(data?.total) || 0);
        })
        .catch((err) => handle401(err));
      listRadiusServers({
        onSuccess: (data) => setRadiusServers(Array.isArray(data) ? data : []),
        onFailure: (err) => handle401(err),
      });
    }

    // PAM session events for the bottom card. The endpoint pages rows and
    // carries no aggregate, so the timeline is derived here from each row's
    // Timestamp (unix seconds).
    if (isPamMode) {
      axios
        .post(
          `${REACT_APP_API_DID_URL}/policyService/AuthenticationLog`,
          { orgId: ORG_ID(), tenantId: DOMAIN_ID(), page: 1, limit: 1000 },
          { headers },
        )
        .then(({ data }) => {
          const points = (data?.logs || [])
            .map((r) => Number(r.Timestamp))
            .filter((t) => Number.isFinite(t) && t > 0)
            .map((t) => ({ tsUnixMs: t * 1000, count: 1 }));
          setPamTimeline(dailyTimeline(points));
        })
        .catch((err) => handle401(err));
    }

    axios
      .post(`${REACT_APP_API_DID_URL}/dashboardService/listEndPointCount`, endpointPayload, { headers })
      .then(({ data }) => setEndpointCount(data?.count))
      .catch(() => {});
    axios
      .post(`${REACT_APP_API_DID_URL}/dashboardService/listUserCount`, endpointPayload, { headers })
      .then(({ data }) => setDirectoryUsersCount(data?.count))
      .catch(() => {});
    axios
      .post(`${REACT_APP_API_DID_URL}/dashboardService/listEndPointUserCount`, endpointPayload, { headers })
      .then(({ data }) => setEndpointUserCount(data?.count))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period, isRadiusMode]);

  const adChartData = useMemo(() => bucketTimeline(authTimeline), [authTimeline]);
  const authChartData = isPamMode ? pamTimeline : adChartData;

  // The chart draws on a canvas, which cannot read the CSS variables.
  const { palette: chartPalette, mode: chartMode } = useAnTheme();
  const authAreaConfig = {
    data: authChartData,
    xField: "label",
    yField: "count",
    height: 240,
    autoFit: true,
    shapeField: "smooth",
    style: { fill: `l(270) 0:${chartPalette.primary}00 1:${chartPalette.primary}55` },
    line: { style: { stroke: chartPalette.primary, strokeWidth: 2 } },
    theme: chartMode === "dark" ? "classicDark" : "classic",
    axis: { x: { labelSpacing: 4 } },
    legend: false,
  };

  const authSpark = authTimeline.map((b) => Number(b.count) || 0);

  const mfaRequiredCount = isRadiusMode && radiusChallenged != null ? radiusChallenged : authCounts.mfa_required;
  const serversActive = (radiusServers || []).filter(serverIsActive).length;
  const serversTotal = (radiusServers || []).length;
  const serversInactive = serversTotal - serversActive;

  return (
    <div
      style={{
        fontFamily: AN_FONT,
        color: AN_COLORS.ink,
        display: "flex",
        flexDirection: "column",
        gap: 16,
        padding: 12,
      }}
    >
      {notificationContext}

      {/* Page header */}
      <div
        style={{
          display: "flex",
          alignItems: "flex-end",
          justifyContent: "space-between",
        }}
      >
        <div>
          <div
            style={{
              fontFamily: AN_MONO,
              fontSize: 10.5,
              letterSpacing: 1.4,
              textTransform: "uppercase",
              color: AN_COLORS.muted,
              marginBottom: 6,
            }}
          >
            Admin · Dashboard
          </div>
          <h1
            style={{
              margin: 0,
              fontSize: 22,
              fontWeight: 600,
              letterSpacing: -0.4,
              color: AN_COLORS.ink,
            }}
          >
            Authentication Monitoring Dashboard
          </h1>
          {(() => {
            const userEmail = localStorage.getItem("UserName");
            if (!userEmail) return null;
            return (
              <div style={{ fontSize: 13, color: AN_COLORS.muted, marginTop: 4 }}>
                Signed in as <span style={{ fontFamily: AN_MONO }}>{userEmail}</span>
              </div>
            );
          })()}
        </div>
      </div>

      {isShowTour && (
        <div>
          <SetupTourChecklist setIsShowTour={setIsShowTour} />
        </div>
      )}

      {/* ── Tenant Status ── */}
      <SectionHeader
        title="Tenant Status"
        subtitle="Compliance & directory inventory"
        icon={ShieldCheck}
        accent={AN_COLORS.ink3}
      />

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
          gap: 12,
        }}
      >
        <KPI
          label="Compliance Mode"
          value={tenantData || "—"}
          icon={ShieldCheck}
          accent={AN_COLORS.primary}
        />
        <KPI
          label="Active Policies"
          value={policyCount != null ? policyCount.toLocaleString() : null}
          icon={FileText}
          accent={AN_COLORS.success}
        />
        <KPI
          label="Directory Users"
          value={directoryUsersCount != null ? directoryUsersCount.toLocaleString() : null}
          icon={UserPlus}
          accent={AN_COLORS.primary}
        />
        <KPI
          label="Total Endpoints"
          value={endpointCount != null ? endpointCount.toLocaleString() : null}
          icon={Monitor}
          accent={AN_COLORS.warn}
        />
        <KPI
          label="Endpoint Users"
          value={endpointUserCount != null ? endpointUserCount.toLocaleString() : null}
          icon={UsersRound}
          accent={AN_COLORS.success}
        />
        {isRadiusMode && (
          <button
            type="button"
            onClick={() => navigate("/pam/radius/servers")}
            aria-label="RADIUS servers: open the RADIUS Servers page"
            style={{ cursor: "pointer", display: "block", width: "100%", padding: 0, border: "none", background: "transparent", textAlign: "left", borderRadius: 10, fontFamily: AN_FONT, color: "inherit" }}
          >
            <KPI
              label="RADIUS servers"
              value={radiusServers ? `${serversActive} / ${serversTotal} active` : null}
              sub={
                radiusServers == null
                  ? null
                  : serversTotal === 0
                    ? "No RADIUS servers yet"
                    : serversInactive > 0
                      ? `${serversInactive} inactive · View servers`
                      : "View servers"
              }
              icon={Server}
              accent={serversInactive > 0 ? AN_COLORS.warn : AN_COLORS.primary}
              warning={serversInactive > 0}
            />
          </button>
        )}
      </div>

      {/* Hero KPI block — AD authentication summary */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1.6fr 1fr 1fr",
          gridTemplateRows: "auto auto",
          gap: 12,
        }}
      >
        {/* Big Total Authentications card with area sparkline (spans two rows) */}
        <div
          style={{
            gridColumn: "1",
            gridRow: "1 / span 2",
            background: "var(--an-surface)",
            border: `1px solid ${AN_COLORS.line}`,
            borderRadius: 10,
            boxShadow: "0 1px 2px rgba(16,22,60,.03)",
            padding: 18,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div style={{ fontSize: 12.5, color: AN_COLORS.muted, fontWeight: 500 }}>
              Total Authentications
            </div>
            <span
              style={{
                width: 28,
                height: 28,
                borderRadius: 8,
                background: `${anAlpha(AN_COLORS.primary, 8)}`,
                color: AN_COLORS.primary,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <ShieldCheck size={15} />
            </span>
          </div>
          <div
            style={{
              fontSize: 44,
              fontWeight: 600,
              letterSpacing: -1.2,
              color: AN_COLORS.primary,
              lineHeight: 1.1,
              marginTop: 6,
            }}
          >
            {authTotal.toLocaleString()}
          </div>
          <div style={{ marginTop: "auto", paddingTop: 14 }}>
            {authSpark.length > 1 ? (
              <Sparkline data={authSpark} fluid h={90} color={AN_COLORS.primary} />
            ) : null}
          </div>
        </div>

        <div style={{ gridColumn: "2", gridRow: "1" }}>
          <KPI
            label="Allowed"
            value={authCounts.allow.toLocaleString()}
            icon={Check}
            accent={DECISION_META.allow.color}
          />
        </div>
        <div style={{ gridColumn: "3", gridRow: "1" }}>
          <KPI
            label="Denied"
            value={authCounts.deny.toLocaleString()}
            icon={X}
            accent={DECISION_META.deny.color}
          />
        </div>
        <div style={{ gridColumn: "2", gridRow: "2" }}>
          <KPI
            label="MFA required"
            value={mfaRequiredCount.toLocaleString()}
            icon={Shield}
            accent={DECISION_META.mfa_required.color}
          />
        </div>
        <button
          type="button"
          onClick={() => navigate("/pam/auth-logs")}
          style={{
            gridColumn: "3",
            gridRow: "2",
            cursor: "pointer",
            textAlign: "left",
            padding: 18,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            gap: 8,
            height: "100%",
            background: AN_COLORS.primary,
            border: `1px solid ${AN_COLORS.primary}`,
            borderRadius: 10,
            boxShadow: "0 1px 2px rgba(16,22,60,.03)",
            fontFamily: AN_FONT,
          }}
        >
          <span
            style={{
              width: 26,
              height: 26,
              borderRadius: 7,
              background: "rgba(255,255,255,.18)",
              color: "#fff",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <ScrollText size={14} />
          </span>
          <div style={{ display: "flex", alignItems: "center", gap: 6, color: "#fff" }}>
            <span style={{ fontSize: 14, fontWeight: 600 }}>View all access logs</span>
            <ArrowRight size={15} />
          </div>
          <div style={{ fontSize: 11.5, color: "rgba(255,255,255,.8)" }}>
            Open the full authentication log
          </div>
        </button>
      </div>

      <Card
        title={isPamMode ? "PAM Session Activity" : "AD Authentication Activity"}
        subtitle={isPamMode ? "All-time · PAM session events" : "All-time · authentication events"}
        action={
          <button
            type="button"
            onClick={() => navigate("/pam/auth-logs")}
            style={{
              border: "none",
              background: "transparent",
              color: AN_COLORS.primary,
              cursor: "pointer",
              fontSize: 12.5,
              fontWeight: 500,
              fontFamily: AN_FONT,
            }}
          >
            View all →
          </button>
        }
      >
        {authChartData.length ? (
          <Area {...authAreaConfig} />
        ) : (
          <div
            style={{
              height: 200,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: AN_COLORS.muted,
              fontSize: 13,
            }}
          >
            {isPamMode ? "No PAM session events in this period." : "No authentication events in this period."}
          </div>
        )}
      </Card>
    </div>
  );
};

// "/" is shared by every mode. PAM gets its own dashboard (/dashboard/summary);
// the others keep the one above. The sidebar's mode switch writes
// localStorage.currentMode and fires "storage", so this follows it live.
const readMode = () => getLocalStorage("currentMode") || "";

const Dashboard = () => {
  const [mode, setMode] = useState(readMode);

  useEffect(() => {
    const onStorage = () => setMode(readMode());
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  // Applications has its own dashboard.
  if (mode === "mfa_apps") return <Navigate to="/pam/applications/dashboard" replace />;
  return PAM_MODES.includes(mode) ? <PamDashboard /> : <LegacyDashboard mode={mode} />;
};

export default Dashboard;
